import { Query, QueryFunctionContext, QueryKey, UseQueryResult } from '@tanstack/react-query';
import {
  compact,
  difference,
  filter,
  find,
  fromPairs,
  intersection,
  isEmpty,
  isEqual,
  join,
  keyBy,
  map,
  maxBy,
  size,
  uniq,
} from 'es-toolkit/compat';
import { FileMetaData } from 'hyparquet';
import { useMemo } from 'react';

import { isNotPresignedUrl } from './api';
import { getParquetResultKey, setParquetLoadProgress } from './parquetLoadProgress';
import { getParquetResourceId, measureParquetStage } from './parquetWorker/instrumentation';
import {
  asyncBufferFromUrlWithGetFallback,
  byteLengthFromUrlWithGet,
  cachedAsyncBufferWithFixedWindows,
} from './parquetWorker/rangeBuffer';
import { getParquetColumnGroupKey, ParquetFile, ParquetMetadataReadRequest, ParquetRow } from './parquetWorker/types';
import { canRefreshParquetUrl } from './parquetWorker/requestError';
import { parquetWorkerClient } from './parquetWorker/workerClient';
import queryClient, { stabilizedQueriesDependency } from './queryClient';
import {
  createParquetUrlRecoveryState,
  ParquetUrlRecoveryState,
  readWithParquetUrlRecovery,
} from './queryHooks/parquetUrlRecovery';
import { useQueriesWithErrorAndRetrySnackbar } from './useQueryWithErrorAndRetrySnackbar';

export { asyncBufferFromUrlWithGetFallback, byteLengthFromUrlWithGet, cachedAsyncBufferWithFixedWindows };
export type { ParquetFile, ParquetRow };

const INDEX_OF_COLUMNS_IN_QUERY_KEY = 3;

interface MetadataRecoveryContext {
  state: ParquetUrlRecoveryState;
  rowQueries: WeakSet<Query>;
}

const metadataRecoveryContexts = new WeakMap<Query, MetadataRecoveryContext>();

const getMetadataRecoveryContext = (
  queryKey: QueryKey,
  urls: ParquetMetadataReadRequest,
  restart = false
): MetadataRecoveryContext => {
  const query = queryClient.getQueryCache().find(queryKey, { exact: true });
  const existing = query && metadataRecoveryContexts.get(query);
  if (existing && !restart) return existing;
  const context = { state: createParquetUrlRecoveryState(urls), rowQueries: new WeakSet<Query>() };
  if (query) metadataRecoveryContexts.set(query, context);
  return context;
};

export interface ParquetUrlAndColumns {
  urlForGetRequest: string;
  urlForHeadRequest?: string;
  // Certain heatmaps visualize data from multiple columns, so we will store their state according to this grouping
  columnsGroupForHeatmap?: string[];
  // Of the heatmap group, which columns enabled by the user
  columnsForQuery?: string[];
  optionalColumnsForQuery?: string[];
  // Is the query enabled by the user
  enabled: boolean;
  // Warm lightweight parquet metadata for faster activation without holding row arrays in memory
  prefetch?: boolean;
  additionalRowContext?: Record<string, any>;
  // In-memory result shape. Parquet remains columnar on disk in either mode;
  // use `columnar` for typed-array/GPU consumers and `rows` for record-oriented consumers.
  outputFormat?: 'rows' | 'columnar';
}
export interface ParquetFileWithQueryContext extends ParquetFile, Partial<ParquetUrlAndColumns> {
  isLoading: boolean;
  dataUpdatedAt: number;
}

const canQueryParquet = (url: string) => Boolean(url) && !isNotPresignedUrl(url);

const getParquetWarmCacheKey = ({
  urlForGetRequest,
  urlForHeadRequest,
}: Pick<ParquetUrlAndColumns, 'urlForGetRequest' | 'urlForHeadRequest'>): string =>
  `${urlForGetRequest}::${urlForHeadRequest || ''}`;

export const useParquetFileQueries = (parquetQueryInputs: ParquetUrlAndColumns[], debug: boolean = false) => {
  const parquetFilesToWarm = compact(
    Object.values(
      keyBy(
        map(
          filter(parquetQueryInputs, ({ enabled, prefetch }) => enabled || prefetch),
          ({ urlForGetRequest, urlForHeadRequest }) => ({
            cacheKey: getParquetWarmCacheKey({ urlForGetRequest, urlForHeadRequest }),
            urlForHeadRequest,
            urlForGetRequest,
          })
        ),
        'cacheKey'
      )
    )
  );
  const activeParquetQueryInputs = filter(
    parquetQueryInputs,
    ({ urlForGetRequest, enabled }) => enabled && canQueryParquet(urlForGetRequest)
  );
  const metadataQueries = useQueriesWithErrorAndRetrySnackbar<Pick<FileMetaData, 'schema' | 'num_rows'>>({
    queries: map(parquetFilesToWarm, ({ urlForGetRequest, urlForHeadRequest }) => ({
      queryKey: ['parquetMetadata', urlForGetRequest, urlForHeadRequest],
      queryFn: ({ signal, queryKey }: QueryFunctionContext) => {
        const urls = { parquetFileUrl: urlForGetRequest, headUrl: urlForHeadRequest };
        const recovery = getMetadataRecoveryContext(queryKey, urls, true);
        const read = (request: typeof urls) => parquetWorkerClient.readMetadata(request, signal);
        // Active column queries own recovery, including a failed metadata prerequisite.
        // Do not spend a second signing budget on their background warm-up.
        const hasActiveQuery = activeParquetQueryInputs.some(
          (input) => getParquetWarmCacheKey(input) === getParquetWarmCacheKey({ urlForGetRequest, urlForHeadRequest })
        );
        return hasActiveQuery ? read(urls) : readWithParquetUrlRecovery(urls, read, signal, recovery.state);
      },
      enabled: canQueryParquet(urlForGetRequest),
      retry: false,
      queriedEntityName: 'parquet file metadata',
      consoleOnly: true,
    })),
  });
  const metadataQueriesByWarmCacheKey = keyBy(
    map(parquetFilesToWarm, ({ cacheKey }, index) => ({ cacheKey, query: metadataQueries[index] })),
    'cacheKey'
  );
  const queries: UseQueryResult<ParquetFile, any>[] = useQueriesWithErrorAndRetrySnackbar({
    snackbarOptions: { persist: true, preventDuplicate: true, variant: 'error' },
    queries: map(activeParquetQueryInputs, (parquetQueryInput) => {
      const resultKey = getParquetResultKey(parquetQueryInput);
      const reportProgress = (loadedFraction: number) => setParquetLoadProgress(resultKey, loadedFraction);
      const {
        urlForGetRequest,
        urlForHeadRequest,
        columnsGroupForHeatmap,
        additionalRowContext,
        outputFormat,
        optionalColumnsForQuery,
      } = parquetQueryInput;
      // If no columns are specified, use the columns group for heatmap
      let columnsForQuery = parquetQueryInput.columnsForQuery || columnsGroupForHeatmap;
      const metadataQuery =
        metadataQueriesByWarmCacheKey[getParquetWarmCacheKey({ urlForGetRequest, urlForHeadRequest })]?.query;
      const metadataKey = ['parquetMetadata', urlForGetRequest, urlForHeadRequest];
      if (!isEmpty(optionalColumnsForQuery) && metadataQuery?.data) {
        columnsForQuery = uniq([
          ...(columnsForQuery || []),
          ...intersection(optionalColumnsForQuery, map(metadataQuery.data.schema, 'name')),
        ]);
      }
      // Until schema recovery settles, optional selections must not reuse a required-only cache entry.
      const queryColumns =
        !isEmpty(optionalColumnsForQuery) && !metadataQuery?.data
          ? uniq([...(columnsForQuery || []), ...(optionalColumnsForQuery || [])])
          : columnsForQuery;
      const queryKey = ['parquet', urlForGetRequest, urlForHeadRequest, queryColumns, outputFormat];
      const cachedQueryResultsForUrl = filter(
        queryClient.getQueryCache().findAll(['parquet', urlForGetRequest]),
        (query) => !isEqual(query.queryKey, queryKey) && query.queryKey[4] === outputFormat && Boolean(query.state.data)
      ) as Query<ParquetFile, any>[];
      // If a previous query already contained the columns we are querying, use that query
      // to avoid re-querying the same columns
      const queryContainingColumns = find(cachedQueryResultsForUrl, (query) => {
        const cachedColumns = query.queryKey?.[INDEX_OF_COLUMNS_IN_QUERY_KEY] as string[] | undefined;
        return isEqual(intersection(cachedColumns, queryColumns), queryColumns);
      });

      const queryResultsWithMaxIntersectionSize = maxBy(cachedQueryResultsForUrl, (query) =>
        size(intersection(query.queryKey[INDEX_OF_COLUMNS_IN_QUERY_KEY] as string[], columnsForQuery))
      );

      let recoveredMetadata: Pick<FileMetaData, 'schema' | 'num_rows'> | undefined;
      const readParquetQuery = async (
        urls: { parquetFileUrl: string; headUrl?: string },
        signal?: AbortSignal
      ): Promise<ParquetFile> => {
        if (!isEmpty(optionalColumnsForQuery) && metadataQuery?.isError) {
          if (!canRefreshParquetUrl(metadataQuery.error)) throw metadataQuery.error;
          // A manual row retry must also recover its failed metadata prerequisite.
          const metadata = await parquetWorkerClient.readMetadata(urls, signal);
          recoveredMetadata = metadata;
          columnsForQuery = uniq([
            ...(parquetQueryInput.columnsForQuery || columnsGroupForHeatmap || []),
            ...intersection(optionalColumnsForQuery, map(metadata.schema, 'name')),
          ]);
        }
        const existingData = queryResultsWithMaxIntersectionSize?.state.data;
        if (!existingData) {
          if (debug) {
            console.debug(
              `No existing data for parquet file ${urlForGetRequest} with columns ${join(
                columnsForQuery,
                ', '
              )}, querying all columns`,
              {
                urlForGetRequest,
                columnsForQuery,
                additionalRowContext,
              }
            );
          }
          // If no existing data, read all columns
          return parquetWorkerClient.readRows(
            {
              ...urls,
              columnsForQuery,
              additionalRowContext,
              outputFormat,
            },
            signal,
            reportProgress
          );
        }
        // Only query for columns that are not already in the existing data
        const existingColumns = existingData.columnsQueried;
        const newColumnsToQuery = difference(columnsForQuery, existingColumns);
        if (isEmpty(newColumnsToQuery)) {
          if (debug) {
            console.debug(
              `No new columns to query for parquet file ${urlForGetRequest}, returning existing data`,
              existingData
            );
          }
          return existingData; // No new columns to query, return existing data
        }
        if (debug) {
          console.debug(`Querying new columns for parquet file ${urlForGetRequest}: ${join(newColumnsToQuery, ', ')}`, {
            urlForGetRequest,
            newColumnsToQuery,
            existingColumns,
            additionalRowContext,
          });
        }
        // If there are new columns to query, read only those columns
        // and merge the results with the existing data
        // This is to avoid re-querying the same columns and to optimize performance
        // Decode missing columns and assemble full rows behind the worker boundary.
        if (debug) {
          console.debug(
            `Merging results for parquet file ${urlForGetRequest} with new columns ${join(newColumnsToQuery, ', ')}`,
            {
              existingColumns,
              existingData,
            }
          );
        }
        const mergedResults = await parquetWorkerClient.readRows(
          {
            ...urls,
            columnsForQuery: newColumnsToQuery,
            baseFile: existingData,
            outputFormat,
          },
          signal,
          reportProgress
        );
        if (debug) {
          console.debug(
            `Merged results for parquet file ${urlForGetRequest} with new columns ${join(newColumnsToQuery, ', ')}`,
            mergedResults
          );
        }
        return mergedResults;
      };

      return {
        queriedEntityName: `parquet file with columns ${join(columnsForQuery, ', ')}`,
        initialData: queryContainingColumns?.state.data,
        placeholderData: queryResultsWithMaxIntersectionSize?.state.data,
        queryKey,
        queryFn: async ({ signal }: QueryFunctionContext): Promise<ParquetFile> => {
          const urls = { parquetFileUrl: urlForGetRequest, headUrl: urlForHeadRequest };
          const recovery = getMetadataRecoveryContext(metadataKey, urls);
          const rowQuery = queryClient.getQueryCache().find(queryKey, { exact: true });
          if (rowQuery && recovery.rowQueries.has(rowQuery)) {
            recovery.state = createParquetUrlRecoveryState(recovery.state.urls);
          }
          if (rowQuery) recovery.rowQueries.add(rowQuery);
          try {
            const data = await readWithParquetUrlRecovery(
              urls,
              (requestUrls) => readParquetQuery(requestUrls, signal),
              signal,
              recovery.state
            );
            if (recoveredMetadata) {
              // Keep the provisional observer holding the data until the resolved observer reuses it.
              queryClient.setQueryData(queryKey, data);
              queryClient.setQueryData(metadataKey, recoveredMetadata);
            }
            return data;
          } finally {
            setParquetLoadProgress(resultKey);
          }
        },
        enabled: isEmpty(optionalColumnsForQuery) || Boolean(metadataQuery?.data) || Boolean(metadataQuery?.isError),
        cacheTime: 0,
        retry: false,
      };
    }),
  });

  const activeQueriesByKey = keyBy(
    map(activeParquetQueryInputs, (parquetQueryInput, index) => ({
      resultKey: getParquetResultKey(parquetQueryInput),
      query: queries[index],
    })),
    'resultKey'
  );

  return useMemo(
    () =>
      map(
        parquetQueryInputs,
        (parquetQueryInput) =>
          activeQueriesByKey[getParquetResultKey(parquetQueryInput)]?.query ||
          ({ data: undefined, dataUpdatedAt: 0, isLoading: false } as UseQueryResult<ParquetFile, any>)
      ),
    [parquetQueryInputs, activeQueriesByKey]
  );
};

/**
 * Fetch multiple parquet files and columns in parallel, and return the results as a hierarchical dictionary of parquet files by URL and
 * then by the column groups they belong to (regardless of the specific columns requested).
 *
 * @param {ParquetUrlAndColumns[]} parquetQueryInputs
 * @returns {{ parquetFiles: { [url: string]: { [columnsGroupForHeatmap: join(columnsGroupForHeatmap, ',')]: {
 *  rows: ParquetRow[];
 *  metadata: FileMetaData;
 *  isLoading: boolean;
 *  urlForGetRequest: string;
 *  urlForHeadRequest: string;
 *  columnsGroupForHeatmap: string[];
 *  columnsForQuery: string[];
 * } } } }}
 */
export const useParquetFilesByColumnGroup = (parquetQueryInputs: ParquetUrlAndColumns[]) => {
  const parquetFileQueries = useParquetFileQueries(parquetQueryInputs);
  const stabilizedParquetQueryInputsDependency = JSON.stringify(parquetQueryInputs);
  // A failed or settled read changes no dataUpdatedAt, so track its loading inputs too.
  const parquetQueryLoadingStateDependency = JSON.stringify(
    map(parquetFileQueries, (query) => [Boolean(query?.data), query?.isFetching, query?.isError])
  );

  const parquetFiles: Record<string, Record<string, ParquetFileWithQueryContext>> = useMemo(() => {
    const parsedResults = map(parquetFileQueries, (query, index) => {
      const matchingQuery = parquetQueryInputs[index];
      const rows = query?.data?.rows || [];
      return {
        columnsQueried: query?.data?.columnsQueried,
        rows,
        columnarData: query?.data?.columnarData,
        metadata: query?.data?.metadata,
        // An enabled query waiting on its byte length or footer has no data yet and is still loading.
        isLoading:
          canQueryParquet(matchingQuery?.urlForGetRequest) &&
          Boolean(matchingQuery?.enabled) &&
          (Boolean(query?.isFetching) || (!query?.data && !query?.isError)),
        dataUpdatedAt: query?.dataUpdatedAt,
        ...(matchingQuery || {}),
      };
    });

    const parquetUrls = compact(uniq(map(parquetQueryInputs, 'urlForGetRequest')));

    return fromPairs(
      map(parquetUrls, (urlForGetRequest) => {
        const resultsWithUrl = filter(parsedResults, (result) => result.urlForGetRequest === urlForGetRequest);
        // We key by all the group's columns, even if we query only a subset of them
        return [
          urlForGetRequest,
          measureParquetStage(
            {
              resourceId: getParquetResourceId(urlForGetRequest),
              stage: 'downstream-consumption',
              thread: 'main',
              rowCount: resultsWithUrl.reduce(
                (sum, result) => sum + (result.columnarData?.length ?? result.rows.length),
                0
              ),
              columnCount: resultsWithUrl.reduce((sum, result) => sum + size(result.columnsQueried ?? []), 0),
            },
            () =>
              keyBy(resultsWithUrl, ({ columnsGroupForHeatmap, outputFormat }) =>
                getParquetColumnGroupKey(columnsGroupForHeatmap, outputFormat)
              )
          ),
        ];
      })
    );
  }, [
    stabilizedQueriesDependency(parquetFileQueries),
    stabilizedParquetQueryInputsDependency,
    parquetQueryLoadingStateDependency,
  ]);

  return { parquetFiles, parquetFileQueries, parquetQueryInputs };
};
